import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { sportInfo, formatMatchTime, normalizeWa } from "../lib/api";
import { useAuth } from "../lib/auth";
import PageShell from "../components/PageShell";
import BottomNav from "../components/BottomNav";
import EmptyState from "../components/EmptyState";
import EditProfileModal from "../components/EditProfileModal";
import PlayerCardShare from "../components/PlayerCardShare";
import { LogOut, MapPin, Shield, Trophy, Calendar, Pencil, MessageSquare, Users as UsersIcon, ChevronRight, IdCard } from "lucide-react";
import { Link } from "react-router-dom";

export default function Profile() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showCard, setShowCard] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get(`/users/${user.user_id}`);
        setHistory(data.history);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (!user) return null;

  const upcoming = history.filter(m => ["OPEN", "FULL"].includes(m.status));
  const past = history.filter(m => ["FINISHED", "CANCELLED"].includes(m.status));

  return (
    <PageShell>
      <header className="px-4 pt-6 pb-2 flex items-center justify-between">
        <div className="font-heading text-2xl font-black tracking-tight">Profil</div>
        <div className="flex items-center gap-2">
          <button
            data-testid="share-card-btn"
            onClick={() => setShowCard(true)}
            className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center text-foreground hover:border-primary/40"
            aria-label="Bagikan kartu pemain"
          >
            <IdCard className="h-5 w-5" />
          </button>
          <button
            data-testid="edit-profile-btn"
            onClick={() => setShowEdit(true)}
            className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center text-foreground hover:border-primary/40"
            aria-label="Edit profil"
          >
            <Pencil className="h-5 w-5" />
          </button>
          <button data-testid="logout-btn" onClick={handleLogout} className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center text-muted-foreground hover:text-destructive">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <section className="px-4 mt-4">
        <div className="bg-card border border-border rounded-3xl p-6 relative overflow-hidden">
          <div aria-hidden className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
          <div className="flex items-center gap-4 relative">
            <div className="h-20 w-20 rounded-full overflow-hidden bg-secondary border-2 border-primary/40 glow-cyan">
              {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-3xl font-bold">{user.name?.[0]}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl font-black tracking-tight truncate">{user.name}</h1>
              <div className="text-sm text-muted-foreground">@{user.username || user.name?.toLowerCase().replace(/\s+/g, "_")}</div>
              {user.city && (
                <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {user.city}
                </div>
              )}
              {user.whatsapp && (
                <a
                  href={`https://wa.me/${normalizeWa(user.whatsapp)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="profile-wa-link"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-1 rounded-full hover:bg-emerald-500/20"
                >
                  <MessageSquare className="h-3 w-3" /> +{normalizeWa(user.whatsapp)}
                </a>
              )}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3 relative">
            <Stat icon={Trophy} label="Match" value={user.matches_played || 0} />
            <Stat icon={Calendar} label="Hadir" value={user.matches_attended || 0} />
            <Stat icon={Shield} label="Reliability" value={`${(user.reliability_score ?? 100).toFixed(0)}%`} />
          </div>
        </div>
      </section>

      {/* Sports */}
      {user.sports?.length > 0 && (
        <section className="px-4 mt-6">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Olahraga</div>
          <div className="flex flex-wrap gap-2">
            {user.sports.map((sid) => {
              const s = sportInfo(sid);
              const level = user.skill_levels?.[sid] || "intermediate";
              return (
                <div key={sid} className="bg-card border border-border rounded-full px-3 py-2 flex items-center gap-2">
                  <span>{s.icon}</span>
                  <span className="text-sm font-bold">{s.label}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full">{level}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Teams entry */}
      <section className="px-4 mt-6">
        <Link
          to="/teams"
          data-testid="profile-teams-link"
          className="block bg-card border border-border rounded-2xl px-4 py-3.5 flex items-center gap-3 hover:border-primary/40"
        >
          <div className="h-10 w-10 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <UsersIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">Teams & Crews</div>
            <div className="text-xs text-muted-foreground">Identitas organizer & recurring matches</div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </Link>
      </section>

      {/* Upcoming */}
      <section className="px-4 mt-6">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Match Mendatang</div>
        {loading ? (
          <div className="h-20 rounded-2xl bg-card animate-pulse" />
        ) : upcoming.length === 0 ? (
          <EmptyState icon="📅" title="Belum ada match" body="Cari atau buat match baru" />
        ) : (
          <div className="space-y-2">
            {upcoming.map((m) => <MiniMatch key={m.match_id} match={m} />)}
          </div>
        )}
      </section>

      {/* Past */}
      {past.length > 0 && (
        <section className="px-4 mt-6">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Histori</div>
          <div className="space-y-2">
            {past.slice(0, 10).map((m) => <MiniMatch key={m.match_id} match={m} />)}
          </div>
        </section>
      )}

      <BottomNav />
      {showEdit && (
        <EditProfileModal
          user={user}
          onClose={() => setShowEdit(false)}
          onSaved={(u) => setUser(u)}
        />
      )}
      {showCard && (
        <PlayerCardShare player={user} onClose={() => setShowCard(false)} />
      )}
    </PageShell>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-secondary/60 border border-border rounded-2xl p-3 text-center">
      <Icon className="h-4 w-4 text-primary mx-auto" />
      <div className="font-heading text-xl font-black mt-1">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function MiniMatch({ match }) {
  const s = sportInfo(match.sport);
  return (
    <Link to={`/match/${match.match_id}`} className="block bg-card border border-border rounded-2xl p-3 flex items-center gap-3 hover:border-primary/40">
      <div className="h-10 w-10 rounded-2xl flex items-center justify-center text-xl" style={{ background: s.color + "22", border: `1px solid ${s.color}44` }}>{s.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{s.label} · {match.venue}</div>
        <div className="text-xs text-muted-foreground">{formatMatchTime(match.starts_at)}</div>
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${match.status === "FULL" ? "bg-emerald-400/10 text-emerald-400" : match.status === "FINISHED" ? "bg-secondary text-muted-foreground" : "bg-primary/10 text-primary"}`}>
        {match.status}
      </span>
    </Link>
  );
}
